import { Color, Label, Root, Series, VerticalLayout, color } from "@amcharts/amcharts5";
import { Axis, AxisRendererX, AxisRendererY, ColumnSeries, XYChart } from "@amcharts/amcharts5/xy";
import { cloneDeep } from "lodash";
import { VuetifyThemeVariant } from "vuetify/types/services/theme";

import ChartWithData from "@/classes/BaseChart/ChartWithData";
import ChartWithDataExport from "@/classes/BaseChart/ChartWithDataExport";
import ChartWithFormatters from "@/classes/BaseChart/ChartWithFormatters";
import ChartWithLegend from "@/classes/BaseChart/ChartWithLegend";
import ChartWithOther from "@/classes/BaseChart/ChartWithOther";
import ChartWithTooltip from "@/classes/BaseChart/ChartWithTooltip";
import { DATA_MODE, EXPLORE_MODULE, PLAN_EXPLORE_MODULE } from "@/constants";
import { mapDimensionValueLabels } from "@/helpers/dimensionLabels";
import extendClass from "@/helpers/extendClass";
import { app } from "@/main";
import { widgetApi } from "@/router";
import { chartsSettings, getChartAllControls } from "@/settings/charts";
import { getColor, getPaletteColors } from "@/settings/colors";
import exploreInputsDictionary from "@/settings/inputsDictionary";
import { metricsDictionary as exploreMetricsDictionary } from "@/settings/metricsDictionary";
import planInputsDictionary from "@/settings/planInputsDictionary";
import { metricsDictionary as planMetricsDictionary } from "@/settings/planMetricsDictionary";
import store from "@/store";
import { AmCharts, AmChartsSeries, ChartDataRecord, Config } from "@/types/charts";
import { InputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";

export type Options = { [key: string]: string | boolean };
export type ChartOptions = { [key: string]: string | boolean };
export type ChartControls = Record<string, string | number | boolean | object>;
export type ChartName = string;
export type ChartFamily = string;
export type LegendData = [];
export type ChartDataProviderOptions = { previousYear?: boolean };
export type ChartDataProvider = (chart: BaseChart, options?: ChartDataProviderOptions) => Promise<ChartDataRecord[]>;

/* eslint-disable @typescript-eslint/no-explicit-any */
// Wide variants of settings types.
export type ChartSettings = Record<string, any>;
// Different object types or callback functions.
export type ChartFormatters = Record<string, any>;
// Different AmCharts axes types.
/* eslint-enable @typescript-eslint/no-explicit-any */
export type ModuleName = "plan" | "explore" | "planExplore";

abstract class BaseChart {
  root!: Root;
  chart!: AmCharts;
  series!: { [key: string]: AmChartsSeries | null };
  widget: ContentWidget;
  config!: Config;
  controls!: ChartControls;
  layout!: VerticalLayout;
  chartName!: ChartName;
  chartFamily!: ChartFamily;
  renderers!: {
    x?: AxisRendererX;
    y?: AxisRendererY;
    yPrimary?: AxisRendererY;
    ySecondary?: AxisRendererY;
    xPrimary?: AxisRendererX;
    xSecondary?: AxisRendererX;
  };
  options!: Options;
  settings!: ChartSettings;
  formatters: ChartFormatters;
  data!: ChartDataRecord[];
  responseData!: ChartDataRecord[];

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // Different AmCharts axes types.
  axes!: Record<string, Axis<any>>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  themeColors: Partial<VuetifyThemeVariant>;
  isDark: boolean;
  palette: Color[];

  private declare getFormatter: () => void;
  protected declare showLegend: (
    inputNames: string[],
    legendData: [],
    sortByNames?: boolean,
    isClickable?: boolean
  ) => void;
  protected declare createLegendLabel: (series: Series, metricValue: string) => void;
  protected declare createTooltip: (
    series: Series,
    createParams: (paramsTooltip: Label) => Record<string, unknown>[]
  ) => void;
  protected declare showItems: <T extends Record<string, unknown>>(
    paramData: T[],
    visualField: string,
    metric: string | undefined,
    positionOther?: "first"
  ) => T[];
  protected declare prepareTopPlanModuleData: (
    data: ChartDataRecord[],
    decompositionMetric: string,
    displayMetric: string,
    showItems: number
  ) => ChartDataRecord[];
  private declare cleanData: (data: Record<string, unknown>[]) => Record<string, unknown>[];
  declare prepareDataForExport: () => Record<string, unknown>[];
  declare exportData: (isSingleChart: boolean) => unknown[][];

  // We use 'any' here, because of AmCharts bug, when AmCharts can
  // take object for formatter, but in TS described only string | undefined
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  declare getMetricFormatter: (metricValue: string, config: Record<string, unknown>) => any;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  declare getCompactAxesMetricFormatter: (metricValue: string, data: ChartDataRecord[]) => any;
  declare getPrecisionByDataset: (data: ChartDataRecord[], key: string) => number | undefined;
  declare getDataLabelsFormatterConfig: () => Record<string, unknown>;
  declare shouldUseCompactDataLabels: () => boolean;
  declare getDataLabelsDecimals: () => number | undefined;
  declare getDataLabelsTopLimit: () => number | undefined;

  protected abstract initChart(): void;
  protected createRenderers?(): void;
  protected createAxes?(): void;
  protected createChart?(chartOption?: ChartOptions): void;
  protected createCursor?(): void;
  protected mapData?(data: ChartDataRecord[]): ChartDataRecord[];
  dataProvider!: ChartDataProvider;
  protected module!: string;
  protected settingsModule!: ModuleName;
  protected inputsDictionary!: Record<string, InputDictionary[]>;
  protected metricsDictionary!: InputDictionary[];

  private mergeDictionaries(
    primary: Record<string, InputDictionary[]>,
    secondary: Record<string, InputDictionary[]>
  ): Record<string, InputDictionary[]> {
    const result = {} as Record<string, InputDictionary[]>;
    const keys = new Set([...Object.keys(primary || {}), ...Object.keys(secondary || {})]);

    keys.forEach((key) => {
      const merged = [...(primary[key] || []), ...(secondary[key] || [])];
      result[key] = merged.filter(
        (item, index, arr) => arr.findIndex((candidate) => candidate.value === item.value) === index
      );
    });

    return result;
  }

  private mergeMetricDictionaries(primary: InputDictionary[], secondary: InputDictionary[]): InputDictionary[] {
    return [...primary, ...secondary].filter(
      (item, index, arr) => arr.findIndex((candidate) => candidate.value === item.value) === index
    );
  }

  constructor(widget: ContentWidget, options?: Options, dataProvider?: ChartDataProvider, module = EXPLORE_MODULE) {
    this.options = options ? options : {};
    this.module = module;
    this.widget = widget;
    this.themeColors = app.$vuetify.theme.currentTheme;
    this.isDark = app.$vuetify?.theme.dark;
    this.settings = cloneDeep(widget.settings);
    this.series = {};
    this.formatters = {};
    this.controls =
      cloneDeep(getChartAllControls(widget?.chartType, widget?.family))?.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.name as string]: curr.default,
        }),
        {} as Record<string, string | number | boolean | object>
      ) ?? {};
    this.palette = getPaletteColors(this.settings.palette);
    if (dataProvider) {
      this.dataProvider = dataProvider;
    }

    this.resolveModuleDictionaries();
  }

  private resolveModuleDictionaries(): void {
    this.settingsModule = this.settings?.useExploreData ? PLAN_EXPLORE_MODULE : (this.module as ModuleName);
    this.inputsDictionary =
      this.settingsModule === PLAN_EXPLORE_MODULE
        ? this.mergeDictionaries(exploreInputsDictionary, planInputsDictionary)
        : this.module === EXPLORE_MODULE
        ? exploreInputsDictionary
        : planInputsDictionary;
    this.metricsDictionary =
      this.settingsModule === PLAN_EXPLORE_MODULE
        ? this.mergeMetricDictionaries(exploreMetricsDictionary, planMetricsDictionary)
        : this.module === EXPLORE_MODULE
        ? exploreMetricsDictionary
        : planMetricsDictionary;
  }

  get isDualMode(): boolean {
    return this.settings?.dataMode === DATA_MODE.DUAL;
  }

  async setupChart(): Promise<void> {
    this.getFormatter();
    await this.getData();
    this.createLayout();
    this.initChart();
    this.setZoomOutButton();
  }

  async create(root: Root): Promise<void> {
    const accentColor = color(this.themeColors["accent-global"] as string);

    this.root = root;
    this.root.numberFormatter.set("numberFormat", {});
    root.numberFormatter.set("intlLocales", "en-NY");
    this.root.interfaceColors.setAll({
      primaryButton: accentColor,
      primaryButtonHover: Color.lighten(accentColor, 0.2),
      primaryButtonDown: Color.lighten(accentColor, 0.3),
      primaryButtonText: color(this.themeColors.primary as string),
      secondaryButton: accentColor,
      secondaryButtonHover: Color.lighten(accentColor, 0.2),
      secondaryButtonDown: Color.lighten(accentColor, 0.3),
      secondaryButtonText: color(this.themeColors.primary as string),
      fill: Color.lighten(accentColor, this.isDark ? -0.4 : 0.4),
      primaryButtonStroke: this.isDark ? color(this.themeColors.primary as string) : Color.lighten(accentColor, 0.3),
      secondaryButtonStroke: this.isDark ? color(this.themeColors.primary as string) : Color.lighten(accentColor, 0.3),
    });

    await this.setupChart();
    (
      this.root as Root & {
        _licenseApplied?: () => void;
        _logo?: { set: (key: string, value: boolean) => void };
      }
    )._licenseApplied?.();
    (this.root as Root & { _logo?: { set: (key: string, value: boolean) => void } })._logo?.set("forceHidden", true);
  }

  public loadChart(chartName: string, family: string): void {
    if (!chartsSettings[chartName]) {
      console.warn(`Config file for chart ${chartName} is not found!`);
    }

    this.chartName = chartName;
    this.chartFamily = family;

    this.config = chartsSettings[chartName];
  }

  protected async getData(): Promise<void> {
    store.commit("charts/updateNoDataStatus", { widgetId: this.widget.id, status: false });

    let dirtyResponseData: Record<string, unknown>[] = [];

    if (this.dataProvider) {
      dirtyResponseData = await this.dataProvider(this);
    } else {
      dirtyResponseData = await widgetApi.getChartData(this);
    }

    this.responseData = this.cleanData(dirtyResponseData as Record<string, unknown>[]);
    if (this.module === EXPLORE_MODULE) {
      this.responseData = mapDimensionValueLabels(this.responseData);
    }
    this.data = typeof this.mapData === "function" ? this.mapData(this.responseData) : this.responseData;

    store.commit("charts/updateNoDataStatus", {
      widgetId: this.widget.id,
      status: !this.data.length && !Object.keys(this.data).length,
    });

    store.commit("charts/stopReRenderChart");
  }

  async refreshData(widget: ContentWidget) {
    this.settings = Object.assign({}, widget.settings);
    this.controls = cloneDeep(widget.controls) as Record<string, string | number | boolean>;
    this.resolveModuleDictionaries();

    await this.setupChart();
  }

  setZoomOutButton(): void {
    if (this.chart instanceof XYChart) {
      this.chart.zoomOutButton.set("layer", 500);
      this.chart.zoomOutButton.set("draggable", true);
    }
  }

  protected getFill(series: ColumnSeries): Color | undefined {
    const seriesFill = series.get("fill");

    return seriesFill
      ? Color.alternative(seriesFill, color(0x000000), color(0xffffff))
      : this.root.interfaceColors.get("alternativeText");
  }

  private createLayout() {
    this.layout =
      this.settings.legendPosition === "top" || this.settings.legendPosition === "bottom"
        ? this.root.verticalLayout
        : this.root.horizontalLayout;
  }

  getSeriesColor(index: number): Color {
    return getColor(this.settings.palette, index % this.palette.length);
  }
}

extendClass(BaseChart, ChartWithFormatters, [
  "getFormatter",
  "getMetricFormatter",
  "getCompactAxesMetricFormatter",
  "getPrecisionByDataset",
  "getDataLabelsFormatterConfig",
  "shouldUseCompactDataLabels",
  "getDataLabelsDecimals",
  "getDataLabelsTopLimit",
]);
extendClass(BaseChart, ChartWithDataExport, ["prepareDataForExport", "exportData"]);
extendClass(BaseChart, ChartWithLegend, ["showLegend", "createLegendLabel"]);
extendClass(BaseChart, ChartWithTooltip, ["createTooltip"]);
extendClass(BaseChart, ChartWithData, ["cleanData", "chartDataFields", "prepareTopPlanModuleData"]);
extendClass(BaseChart, ChartWithOther, ["showItems"]);

export default BaseChart;
